// MaturaRegularPolygonInscribedAngleClosedTask.js
// Zadanie zamknięte: kąt wpisany w okrąg opisany na n-kącie foremnym.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import RegularPolygonInscribedAngleDiagram from "../../../../components/diagrams/RegularPolygonInscribedAngleDiagram";

/* ============================================================
   1. GENEROWANIE KONFIGURACJI WIELOKĄTA
   ========================================================== */

function randomInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Losujemy tylko takie (n, edgesSpan), aby kąt wpisany był CAŁKOWITY:
// inscribedDeg = edgesSpan * 180 / n  ∈ Z
function generatePolygonConfig() {
  for (let tries = 0; tries < 400; tries++) {
    const n = randomInt(5, 12); // od pięciokąta do dwunastokąta

    // Wierzchołki numerujemy: 0 -> A, 1 -> B, ...
    const letters = "ABCDEFGHIJKL".split("");
    const names = letters.slice(0, n);

    // A = 0, drugi punkt to k krawędzi dalej
    const edgesSpan = randomInt(2, Math.min(5, n - 3)); // ile boków obejmuje łuk
    const iLeft = 0; // A
    const iRight = edgesSpan; // np. D, jeśli edgesSpan = 3

    const minCenter = iRight + 2;
    const maxCenter = n - 1;
    const iCenter =
      minCenter <= maxCenter ? randomInt(minCenter, maxCenter) : n - 1;

    const inscribedDeg = (edgesSpan * 180) / n;

    // odrzucamy rzadkie przypadki z połówkami/ćwiartkami
    if (!Number.isInteger(inscribedDeg)) continue;
    // 45° i 90° tworzą zbyt charakterystyczne przypadki specjalne.
    if ([45, 90].includes(inscribedDeg)) continue;

    return {
      n,
      names,
      iLeft,
      iRight,
      iCenter,
      edgesSpan,
      inscribedDeg,
    };
  }

  // awaryjnie – praktycznie nie powinno się zdarzyć
  const n = 10;
  const edgesSpan = 3; // 3*180/10 = 54
  const letters = "ABCDEFGHIJKL".split("");
  const names = letters.slice(0, n);
  return {
    n,
    names,
    iLeft: 0,
    iRight: edgesSpan,
    iCenter: edgesSpan + 2,
    edgesSpan,
    inscribedDeg: (edgesSpan * 180) / n,
  };
}

/* ============================================================
   2. OPCJE ODPOWIEDZI (A–D)
   ========================================================== */

// Szuka m, aby m*(180/n) było CAŁKOWITE, w (0,180), i nie kolidowało z innymi.
function findIntegerEdgeMultiple({ n, correct, banned = [], maxM = 12 }) {
  const base = 180 / n;

  for (let m = 1; m <= maxM; m++) {
    const v = m * base;

    if (!Number.isInteger(v)) continue;
    if (v <= 0 || v >= 180) continue;
    if (v === correct) continue;
    if (banned.includes(v)) continue;

    return v;
  }
  return null;
}

function buildOptions(inscribedDeg, edgesSpan, n) {
  // Tu inscribedDeg jest już całkowite dzięki generatePolygonConfig()
  const correct = inscribedDeg;

  const candidates = [];

  // 1) kąt środkowy dla tego łuku (typowy błąd: nie dzielę przez 2)
  // (też całkowity, bo correct całkowity)
  candidates.push(2 * inscribedDeg);

  // 2) zamiast 180/n (które bywa połówką): m*(180/n) dobrane tak, by było całkowite
  // (sens: "pomyliłem liczbę boków/łuków", ale wciąż kąt wpisany)
  const edgeLike = findIntegerEdgeMultiple({
    n,
    correct,
    banned: candidates,
    maxM: 12,
  });
  if (edgeLike != null) candidates.push(edgeLike);

  // 3) "trochę za dużo" – dodajemy całkowity krok (edgeLike), jeśli się da
  if (edgeLike != null) {
    const v = inscribedDeg + edgeLike;
    if (Number.isInteger(v) && v > 0 && v < 180 && v !== correct) {
      candidates.push(v);
    }
  }

  // 4) 180 - α (mylenie z dopełnieniem)
  candidates.push(180 - inscribedDeg);

  // wybór 3 różnych błędnych
  const wrong = [];
  for (const v of candidates) {
    if (!Number.isInteger(v)) continue;
    if (v === correct) continue;
    if (v <= 0 || v >= 180) continue;
    if (wrong.includes(v)) continue;
    wrong.push(v);
    if (wrong.length === 3) break;
  }

  // awaryjnie domknij zestaw – dokładamy "ładne" całkowite odchyłki
  let extra = 10;
  while (wrong.length < 3) {
    const v = correct + extra;
    if (Number.isInteger(v) && v > 0 && v < 180 && !wrong.includes(v)) {
      wrong.push(v);
    }
    extra += 10;
  }

  const all = shuffle([correct, ...wrong]);
  const correctIndex = all.indexOf(correct);

  const optionsLatex = all.map((v) => `${v}^{\\circ}`);

  return { optionsLatex, correctIndex };
}

/* ============================================================
   3. GENERATE TASK
   ========================================================== */

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 20:
 *   dziesięciokąt foremny ABCDEFGHIJ, kąt wpisany AGD.
 *   Łuk AD obejmuje 3 boki → kąt środkowy 3·36° = 108° → wpisany 54°  (C).
 */
function createJune2025OriginalTask() {
  return {
    type: "MaturaRegularPolygonInscribedAngleClosedTask",
    data: {
      n: 10,
      names: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"],
      iLeft: 0, // A
      iRight: 3, // D
      iCenter: 6, // G
      edgesSpan: 3,
      inscribedDeg: 54,
      optionsLatex: ["18^{\\circ}", "36^{\\circ}", "54^{\\circ}", "60^{\\circ}"],
      correctIndex: 2,
      angleName: "AGD",
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 20,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  const cfg = generatePolygonConfig();
  const { n, names, iLeft, iRight, iCenter, edgesSpan, inscribedDeg } = cfg;

  const { optionsLatex, correctIndex } = buildOptions(inscribedDeg, edgesSpan, n);

  const vertexA = names[iLeft];
  const vertexB = names[iCenter];
  const vertexC = names[iRight];

  return {
    type: "MaturaRegularPolygonInscribedAngleClosedTask",
    data: {
      n,
      names,
      iLeft,
      iRight,
      iCenter,
      edgesSpan,
      inscribedDeg,
      optionsLatex,
      correctIndex,
      angleName: `${vertexA}${vertexB}${vertexC}`,
    },
  };
}

/* ============================================================
   4. RENDER TASK
   ========================================================== */

export function renderTask({ data }) {
  const { n, names, iLeft, iRight, iCenter, optionsLatex, angleName } = data;

  const polygonName = names.join("");

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Na <InlineMath math={`${n}`} />-kącie foremnym{" "}
        <InlineMath math={polygonName} /> opisano okrąg o środku w punkcie{" "}
        <InlineMath math={"S"} /> (zobacz rysunek).
      </p>

      {/* rysunek wycentrowany w poleceniu */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "16px 0",
        }}
      >
        <RegularPolygonInscribedAngleDiagram
          n={n}
          iCenter={iCenter}
          iLeft={iLeft}
          iRight={iRight}
        />
      </div>

      <p>
        Miara kąta wpisanego <InlineMath math={angleName} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {optionsLatex.map((latex, idx) => {
          const letter = ["A", "B", "C", "D"][idx];
          return (
            <div key={letter} style={{ marginBottom: 6 }}>
              <InlineMath math={`${letter}.\\; ${latex}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   5. INPUT ABCD
   ========================================================== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = data.optionsLatex.map((_, idx) => ({
    value: idx,
    label: ["A", "B", "C", "D"][idx],
    textNode: ["A", "B", "C", "D"][idx],
  }));

  const handleChange = (idx) => {
    const letter = ["A", "B", "C", "D"][idx];
    const latex = data.optionsLatex[idx];

    onChange?.({
      choiceIndex: idx,
      label: letter,
      latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* ============================================================
   6. TWOJA ODPOWIEDŹ
   ========================================================== */

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return <InlineMath math={`${label}.\\; ${latex}`} />;
}


/* ============================================================
   7. CHECK / ANSWER / EXPLANATION
   ========================================================== */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const letter = ["A", "B", "C", "D"][data.correctIndex];
  const latex = data.optionsLatex[data.correctIndex];
  return (
    <div>
      <InlineMath math={`${letter}.\\; ${latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { n, edgesSpan, inscribedDeg, angleName } = data;

  // Uwaga: inscribedDeg jest już liczbą całkowitą z generatora.
  // Central per edge może nie być całkowity dla dowolnego n, ale w tym zadaniu zwykle jest OK.
  // Policzymy liczbowo (bez zaokrągleń na siłę); jeśli generator gwarantuje całkowitość, wyjdzie równo.
  const centralPerEdge = 360 / n;
  const centralArc = edgesSpan * centralPerEdge; // kąt środkowy dla łuku
  const halfCentral = centralArc / 2;

  // LaTeX do ładnego zapisu (jak na Twoim „starym” wyjaśnieniu)
  const centralPerEdgeLatex = `\\alpha_{\\text{sr, 1 bok}}=\\frac{360^\\circ}{${n}}=${centralPerEdge}^\\circ`;
  const centralArcLatex = `\\alpha_{\\text{sr}}=${edgesSpan}\\cdot ${centralPerEdge}^\\circ=${centralArc}^\\circ`;
  const inscribedLatex = `\\alpha_{\\text{wpisany}}=\\frac{1}{2}\\alpha_{\\text{sr}}=\\frac{${centralArc}^\\circ}{2}=${halfCentral}^\\circ`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Wyznaczamy kąt środkowy przypadający na jeden bok{" "}
        <InlineMath math={`${n}`} />-kąta foremnego:
      </p>

      <BlockMath math={centralPerEdgeLatex} />

      <p>
        <strong>Krok 2.</strong> Łuk, na którym oparty jest kąt{" "}
        <InlineMath math={angleName} />, obejmuje <InlineMath math={`${edgesSpan}`} /> boków, więc
        odpowiadający mu kąt środkowy ma miarę:
      </p>

      <BlockMath math={centralArcLatex} />

      <p>
        <strong>Krok 3.</strong> Kąt wpisany oparty na tym samym łuku ma miarę równą
        połowie kąta środkowego:
      </p>

      <BlockMath math={inscribedLatex} />

      <p>
        Zatem miara kąta <InlineMath math={angleName} /> wynosi{" "}
        <InlineMath math={`${inscribedDeg}^\\circ`} />.
      </p>
    </div>
  );
}


/* ============================================================
   8. DEFAULT EXPORT
   ========================================================== */

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
